import styled, {css} from 'styled-components'

import StyledButton from '@/components/button'
import {container} from '@/styles/grid'
import {focusStyles} from '@/styles/utils'
import {tokens, colors, headerSizes, zIndex} from '@/theme'
import mq from '@/utils/media-query'

// This file used to back the legacy Header's full nav (dropdowns, mobile menu links, etc.) via
// header-nav-left.tsx/header-nav-right.tsx. Those were removed once HeaderV2 replaced the legacy
// Header for all locales (Task 6), so this file is trimmed to what's still consumed: NavCta and
// mobileMenuBreakpoint (used directly outside this file), plus HeaderWrapper/HeaderInner/
// DesktopLogo/MobileLogo/CTAWrapper (used by the standalone bills-tracker/expense-report-generator
// headers and explore-product) and everything those transitively reference. Nothing anywhere still
// renders a nav link/group/mobile-menu-button, so those styles (and the CSS blocks in HeaderWrapper
// that only ever targeted them) are removed rather than kept as unreachable nested selectors.
const DESKTOP_LOGO_WIDTH_PX = 68

interface WrapperProps {
    $menuOpen?: boolean
    $dropdownOpen?: boolean
}

export const mobileMenuBreakpoint = 'sm'
const tabletMenuBreakpoint = 'md'
const laptopMenuBreakpoint = 'lg'
const mobileMenuWidth = '480px'

const linksFocusStyles = css`
    &:focus {
        ${focusStyles()}
        border-radius: ${tokens.spacing4};
    }
`

export const NavCta = styled(StyledButton)`
    font-size: ${tokens.fontLarge};
    transition: background ${tokens.fastIn};
`

const Logo = styled.div`
    * {
        display: block;
        height: 100%;
    }

    img {
        object-fit: contain;
    }

    a {
        ${linksFocusStyles}
    }
`

export const MobileLogo = styled(Logo)`
    display: none;
    width: 72px;
    padding: ${tokens.spacing10} 0; /* Increases height to match height when sticky CTA shows */
`

export const DesktopLogo = styled(Logo)`
    z-index: ${zIndex.headerLogo};
    display: block;
    width: ${DESKTOP_LOGO_WIDTH_PX}px;
`

export const HeaderInner = styled.div<WrapperProps>`
    ${container}
    display: grid;
    width: 100%;
    padding-right: 0;
    padding-left: 0;
    margin: 0 auto;
    grid-auto-flow: column;
    justify-content: space-between;
    align-items: ${(props) => (props.$menuOpen ? 'start' : 'center')};

    ${mq.min(tabletMenuBreakpoint)} {
        padding-right: 0;
        padding-left: 0;
    }

    ${mq.between(mobileMenuBreakpoint, laptopMenuBreakpoint)} {
        grid-template-columns: ${(props) => (props.$menuOpen ? 'auto' : 'auto 1fr')};
    }

    ${mq.min(laptopMenuBreakpoint)} {
        grid-template-columns: auto 1fr;
        align-items: center;
        padding-right: 0;
        padding-left: 0;
    }
`

// Adding fixed height from theme to accurately position
// elements around header / calculate scroll offsets.
// We bump the z-index when the mobile menu or any dropdown are open
// to ensure they are on top of other elements with the zIndex.header
// i.e the StickyNavSections
export const HeaderWrapper = styled.header<WrapperProps>(({$menuOpen, $dropdownOpen}) => {
    return css`
        position: sticky;
        top: 0;
        left: 0;
        z-index: ${$menuOpen || $dropdownOpen ? zIndex.header + 2 : zIndex.header};
        display: flex;
        width: 100%;
        padding: ${tokens.spacing8} ${tokens.spacing24};
        padding-bottom: ${$menuOpen && '0'};
        align-items: center;
        height: ${$menuOpen ? 'auto' : headerSizes.heightSmall}px;
        background: ${colors.background.default};

        ${mq.max(headerSizes.breakpoint)} {
            overflow: auto;
        }

        ${mq.between(mobileMenuBreakpoint, laptopMenuBreakpoint)} {
            right: 0;
            left: unset;
            display: grid;
            width: ${$menuOpen ? mobileMenuWidth : '100%'};
            align-items: flex-end;
            grid-auto-flow: column;
            grid-template-columns: auto;
            border-bottom-left-radius: ${$menuOpen ? tokens.spacing48 : 0};
        }

        ${mq.min(mobileMenuBreakpoint)} {
            padding-right: ${tokens.spacing24};
            padding-left: ${tokens.spacing24};
        }

        ${mq.min('md')} {
            padding-right: ${tokens.spacing40};
            padding-left: ${tokens.spacing40};
        }

        ${mq.max(headerSizes.breakpoint)} {
            overscroll-behavior: contain;

            & > * + * {
                margin-left: 0;
            }

            ${DesktopLogo} {
                display: none;
            }

            ${MobileLogo} {
                display: block;
            }
        }

        ${mq.min(headerSizes.breakpoint)} {
            height: ${headerSizes.heightLarge}px;
        }
    `
})

export const CTAWrapper = styled.div`
    justify-self: end;
`
